import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui';
import { BriefCard, type BriefStatus } from '../components/dashboard/BriefCard';
import { EmptyDashboard } from '../components/dashboard/EmptyDashboard';
import { CreditsDisplay } from '../components/dashboard/CreditsDisplay';

interface BriefSummary {
  sessionId: string;
  genre: string;
  premiseExcerpt: string;
  status: BriefStatus;
}

interface DashboardData {
  briefs: BriefSummary[];
  credits: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/dashboard', { credentials: 'include' })
      .then((res) => res.json())
      .then((d) => setData(d))
      .catch(() => setData({ briefs: [], credits: 0 }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border px-5 sm:px-8 py-4">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-heading">
            <span className="font-display text-lg">Ghostwright</span>
            <span className="text-primary leading-none" aria-hidden="true">·</span>
          </a>
          <div className="flex items-center gap-4">
            {data && <CreditsDisplay balance={data.credits} />}
            <span className="text-sm text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-[960px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">
                <span className="text-primary mr-1.5" aria-hidden="true">§</span>
                Dashboard
              </p>
              <h1 className="font-display text-[clamp(24px,3.5vw,32px)] font-normal leading-[1.15] tracking-[-0.018em] text-heading [font-variation-settings:'opsz'_96,'SOFT'_60]">
                Your Books
              </h1>
            </div>
            {data && data.briefs.length > 0 && (
              <Button onClick={() => navigate('/brief/new')}>
                New Brief
              </Button>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {!loading && data && data.briefs.length === 0 && <EmptyDashboard />}

          {!loading && data && data.briefs.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.briefs.map((brief) => (
                <BriefCard
                  key={brief.sessionId}
                  id={brief.sessionId}
                  genre={brief.genre}
                  premiseExcerpt={brief.premiseExcerpt}
                  status={brief.status}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-border px-5 sm:px-8 py-6 text-xs text-muted-foreground">
        <div className="max-w-container mx-auto flex items-center justify-between flex-wrap gap-3">
          <span>
            Ghostwright<span className="text-primary mx-1.5" aria-hidden="true">·</span>2026
          </span>
          <span>Dashboard</span>
        </div>
      </footer>
    </div>
  );
}
